import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import {
  supplierAssessmentDraftSchema,
  type SupplierAssessmentDraft,
  type SupplierDocumentType,
  type SupplierDocumentView,
} from '../shared/supplier-assessment';

export function useSupplierAssessment(applicationId: string) {
  const [draft, setState] = useState(() => supplierAssessmentDraftSchema.parse({}));
  const [documents, setDocuments] = useState<SupplierDocumentView[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState('');
  const current = useRef(draft);
  const sequence = useRef(0);
  const revision = useRef(0);
  const initialized = useRef(false);
  const initialization = useRef<Promise<void> | null>(null);
  const flight = useRef<Promise<number> | null>(null);
  const blocked = useRef(false);
  const change = (next: SupplierAssessmentDraft) => {
    current.current = next;
    sequence.current++;
    setState(next);
    setDirty(true);
    if (!blocked.current) setMessage('尚有未儲存的問卷內容');
  };
  const load = async () => {
    if (initialized.current) return;
    if (initialization.current) return initialization.current;
    initialization.current = (async () => {
      const state = await api.vendorProactive.applicationAssessment();
      revision.current = state.revision;
      setDocuments(state.documents);
      if (sequence.current === 0) {
        current.current = state.draft;
        setState(state.draft);
      } else if (state.revision > 0) {
        blocked.current = true;
        throw new Error('發現已保存問卷，請重新開啟申請以避免覆蓋。');
      }
      initialized.current = true;
      setReady(true);
    })().finally(() => {
      initialization.current = null;
    });
    return initialization.current;
  };
  const save = async (): Promise<number> => {
    if (flight.current) await flight.current;
    if (blocked.current)
      throw new Error('問卷尚未保存，請先處理下方錯誤；目前輸入內容仍保留在畫面。');
    setBusy(true);
    const operation = (async () => {
      await load();
      const sentSequence = sequence.current;
      const response = await api.vendorProactive.saveApplicationAssessment(
        revision.current,
        current.current,
      );
      revision.current = response.revision;
      if (sentSequence === sequence.current) {
        setDirty(false);
        setMessage('問卷已儲存');
      }
      return response.revision;
    })();
    flight.current = operation;
    try {
      return await operation;
    } catch (error) {
      blocked.current = true;
      setMessage(error instanceof Error ? error.message : '問卷未儲存，請稍後重試。');
      throw error;
    } finally {
      flight.current = null;
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!applicationId) return;
    void load().catch((error) => {
      blocked.current = true;
      setMessage(error instanceof Error ? error.message : '問卷載入失敗');
    });
    // Application identity is the only trigger; never reload on keystrokes.
  }, [applicationId]);
  const upload = async (type: SupplierDocumentType, file: File, issue: string, expiry: string) => {
    if (!applicationId) {
      setMessage('請先儲存基本資料草稿，再上傳證照。');
      return;
    }
    try {
      await save();
      setBusy(true);
      const state = await api.vendorProactive.uploadAssessmentDocument(type, file, issue, expiry);
      revision.current = state.revision;
      setDocuments(state.documents);
      setMessage('文件已上傳，可於資格及問卷重用');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '文件上傳失敗');
    } finally {
      setBusy(false);
    }
  };
  return {
    draft,
    change,
    documents,
    dirty,
    busy: busy || Boolean(applicationId && !ready && !message),
    message,
    save,
    upload,
    revision,
    retry: () => {
      blocked.current = false;
      void save().catch(() => undefined);
    },
  };
}
